/**
 * QA Console — ชั้น API เดียวของทั้งโมดูล (Q3 · คะแนนจริง Q9 · เลิกข้อมูลจำลอง Q10)
 *
 * ครอบ `api/v1/QaConsole/*` ตามแบบ axios/cookie-auth ที่ ClientApp ใช้อยู่
 * (ดู `services/metaDocApi.js`) · response ทุกตัวห่อด้วย ResponseModel
 * `{ message, status, data }` ฟังก์ชันข้างล่างแกะ `.data` ให้แล้ว
 *
 * Q10 — ชั้นข้อมูลจำลองและชั้นผสมถูก**ลบทั้งชั้น** · ตัวช่วยหน้าจอ (`toListItem` /
 *    `toNbaCard` / `colorOf` …) กับฟังก์ชันที่ component เรียกจริง (`getCases` /
 *    `getCaseDetail` / `getLiveAgents`) ย้ายมาอยู่ท้ายไฟล์นี้ · `index.js` จึงเหลือ
 *    เป็น re-export ตรง ๆ ไม่มีสวิตช์เลือกแหล่งข้อมูลให้เลือกผิดอีกแล้ว
 *
 * ⚠️ ไฟล์นี้คืน **เฉพาะของที่มีจริงในฐานข้อมูล** — ไม่มีเส้นทางไหนที่คืนของแต่ง
 *    ฐานที่ยังไม่มีเคสจะเห็น "ไม่มีข้อมูล" ไม่ใช่ตัวเลขตัวอย่าง (ตั้งใจ: ตัวเลขตัวอย่าง
 *    ที่หน้าตาเหมือนของจริงคือสิ่งที่ทำให้คนตัดสินใจผิดโดยไม่รู้ตัว)
 *
 * ⚠️ ผลประเมินเป็น null ได้เสมอ (เคสยังไม่ปิด · รอคิว worker · ถูกข้าม · ประเมินล้ม)
 *    หน้าจอต้องอ่าน `evalStatus`/`qaStatus` แล้วเล่าสถานะตามจริง ไม่ใช่เดาว่า
 *    "ไม่มีคะแนน = ระบบพัง" — ดูตารางสถานะต่อ element ในแผน Q9 §3.3
 *
 * แผนงาน: docs/qa-console/QA-Console-Backend-Plan.md
 */

import axios from 'axios';
import * as recommendApi from './qaRecommendApi';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * ดัก "route ไม่มีอยู่จริงบน API ตัวที่คุยด้วย" — เหตุผลเต็มอยู่ที่ `qaRecommendApi.js`
 *
 * ⚠️ SPA fallback ของ server ตอบ **200 + index.html** ให้ path ที่ไม่ตรง route ไหนเลย
 *    ถ้าไม่ดักตรงนี้ HTML จะไหลลงไปเป็น "ข้อมูล" แล้วไปพังตอน render แทนที่จะขึ้น error
 *    ที่บอกความจริงว่า API ที่ deploy อยู่ยังไม่มี endpoint นี้
 */
function assertNotSpaFallback(response) {
  const headers = (response && response.headers) || {};
  const contentType = String(headers['content-type'] || headers['Content-Type'] || '');
  const body = response && response.data;
  const looksLikeHtml =
    contentType.includes('text/html') || (typeof body === 'string' && body.trimStart().startsWith('<'));
  if (!looksLikeHtml) return;

  const err = new Error(
    'API ตอบหน้าเว็บแทนข้อมูล — endpoint นี้ไม่มีอยู่บน API ที่กำลังคุยด้วย ' +
      '(ปกติแปลว่า build ที่ deploy อยู่เก่ากว่าฟีเจอร์นี้)'
  );
  err.httpStatus = response && response.status;
  err.isRouteMissing = true;
  throw err;
}

function unwrap(response) {
  assertNotSpaFallback(response);
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) return body.data;
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    throw err;
  }
  return body;
}

function toError(error) {
  // ⚠️ error ที่เราสร้างเองไม่มี `response` — ห่อซ้ำแล้วธง isRouteMissing จะหายไป
  if (error && error.isRouteMissing) return error;

  const body = error && error.response && error.response.data;
  const err = new Error(
    (body && (body.message || body.Message)) || (error && error.message) || 'Request failed'
  );
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw toError(e);
  }
}

async function post(url, payload) {
  try {
    return unwrap(await http.post(url, payload));
  } catch (e) {
    throw toError(e);
  }
}

const ROOT = 'api/v1/QaConsole';

/**
 * เพดานช่วงเวลาต่อคำขอ (ฝั่ง server บีบซ้ำใน `QaAnalyticsFilter.Normalized`)
 *
 * ⚠️ ต้องบีบที่นี่ด้วย ไม่ใช่ปล่อยให้ server บีบฝ่ายเดียว — ตัวเลือก "ทั้งหมด" ของ
 *    QaFilterBar ส่งค่า 9999 ซึ่งไม่ใช่จำนวนวันที่แปลว่าอะไรเลย · ส่งไปตรง ๆ แล้ว
 *    หน้าจอกับ server จะเข้าใจคนละช่วงกัน ตอนขึ้นป้าย "ย้อนหลัง N วัน" จะโกหกทันที
 */
const MAX_WINDOW_DAYS = 365;

/** แปลงตัวเลือกช่วงเวลาของ QaFilterBar (7/30/90/9999) เป็นจำนวนวันที่ server รับได้ */
function toDays(range) {
  const n = Number(range);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(n, MAX_WINDOW_DAYS);
}

/**
 * ตัวกรองร่วม `{ channel, topic, range }` → query ของ endpoint analytics
 *
 * ⚠️ **จุดแปลงเดียวของทั้งโมดูล** — 4 หน้า (Overview / Agents / AgentDetail /
 *    Investigations) ต้องถามด้วยช่วงและตัวกรองชุดเดียวกันเป๊ะ ไม่งั้นตัวเลขบนหน้า
 *    Overview กับจำนวนแถวบนหน้า Investigations จะไม่ตรงกัน แล้วไม่มีใครรู้ว่าใครผิด
 *
 * ⚠️ 'all' ต้อง **ไม่ส่งคีย์เลย** ไม่ใช่ส่ง `caseType=all` — ฝั่ง server เทียบค่าตรง ๆ
 *    ได้ 0 แถวโดยไม่มี error ให้เห็น (บั๊กเดิมของตัวกรองช่องทางเป็นแบบนี้เป๊ะ)
 */
export function toAnalyticsQuery(filter = {}) {
  const params = {};
  const days = toDays(filter.range);
  if (days) params.days = days;
  if (filter.channel && filter.channel !== 'all') params.caseType = filter.channel;
  if (filter.topic && filter.topic !== 'all') params.topicId = filter.topic;
  return params;
}

function toQueryString(params) {
  const parts = Object.entries(params || {})
    .filter(([, v]) => v !== null && v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * ค้นเคสแบบแบ่งหน้า
 *
 * ⚠️ กรอง/เรียง/แบ่งหน้า ทำที่ฝั่ง server ทั้งหมด ตามกฎเหล็กข้อ 6 ของแผน frontend
 *    (ถ้า component กรองเอง พอข้อมูลจริงโตขึ้นจะต้องเขียนหน้าใหม่ทั้งหน้า)
 */
export function searchCases(query = {}) {
  return post(`${ROOT}/cases/search`, {
    // ⚠️ ปุ่ม ALL/VOICE/CHAT ของ QaFilterBar ส่งค่ามาที่ query.channel และค่าที่ส่งคือ
    //    'voice'/'chat' — ต้องไปลงช่อง caseType เท่านั้น
    //    เคยผูกไปที่ channelId (line/facebook/instagram) ผลคือกด "แชท" แล้วได้ 0 แถว
    //    ทั้งที่ทุกเคสในระบบเป็นแชท และไม่มี error อะไรให้เห็นเลย
    caseType: query.channel && query.channel !== 'all' ? query.channel : null,
    channelId: query.channelId && query.channelId !== 'all' ? query.channelId : null,
    agentKey: query.agentId || null,
    status: query.status && query.status !== 'all' ? query.status : null,
    // Q9 — ผลตัดสิน QA (pass|borderline|fail|pending|not-evaluated) · **คนละช่องกับ status**
    //    ซึ่งเป็นวงจรชีวิตของเคส (open|closed) — ของเดิมหน้าจอเคยส่ง pass/fail ลงช่อง status
    //    แล้วได้ 0 แถวเงียบ ๆ จนตัวกรองดูเหมือนพัง ทั้งที่แค่ลงผิดช่อง
    qaStatus: query.qaStatus && query.qaStatus !== 'all' ? query.qaStatus : null,
    from: query.from || null,
    to: query.to || null,
    // Q10 — **แก้บั๊กที่มีมาตลอด**: QaFilterBar ส่งช่วงเวลามาที่ `range` (7/30/90/9999)
    //    แต่ไฟล์นี้เคยอ่านแค่ from/to ซึ่งไม่มีใครเซ็ต → ตัวกรองวันที่บนหน้า Case Explorer
    //    เป็น no-op เงียบ ๆ มาตั้งแต่แรก (ดูเหมือนทำงานเพราะ dropdown เปลี่ยนค่าได้)
    //    · server ใช้ `days` ต่อเมื่อ from/to ว่าง จึงเลือกช่วงแบบระบุวันที่เองได้อยู่
    days: query.from || query.to ? null : toDays(query.range),
    keyword: query.q || null,
    topicId: query.topic && query.topic !== 'all' ? query.topic : null,
    page: query.page || 1,
    pageSize: query.pageSize || 25,
    sortBy: query.sortBy || null,
    sortDir: query.sortDir || null,
  });
}

/** หัวเคส + ประวัติคนเข้า-ออก (Agent Transfer Chat) */
export function getCase(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}`);
}

/**
 * บทสนทนาของเคส
 *
 * ⚠️ endpoint นี้สิทธิ์แคบกว่าตัวอื่น (super admin + admin เท่านั้น) เพราะคืนข้อความ
 *    ที่ลูกค้าธนาคารพิมพ์จริง · ผู้เรียกต้องรับ 401/403 ได้โดยไม่พังทั้งหน้า
 */
export function getTranscript(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/transcript`);
}

/**
 * Q6 — คำแนะนำที่ AI สร้างให้เคสนี้ (สิ่งที่ควรทำ / คำพูด / แปลคำพูด / เหตุผล)
 *
 * สิทธิ์เท่าหัวเคส ไม่ใช่เท่า transcript — ดู APIAuthen.cs
 */
export function getCaseNextBestActions(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/next-best-actions`);
}

/**
 * Q11 — สรุปการสนทนา (ฉบับ AI 5 ช่อง + ฉบับที่ agent แก้แล้วส่งจริง)
 *
 * สิทธิ์เท่า transcript (role 0,1) — เนื้อสรุปเล่าบทสนทนาทั้งใบ · ผู้เรียกต้องรับ
 * 401/403 ได้โดยไม่พังทั้งหน้า (แบบเดียวกับ getTranscript)
 */
export function getCaseSummary(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/summary`);
}

/** Q6 — ค่าตั้งของแท็บ Next best action · ไม่มีแถวในฐาน = backend คืนค่าตั้งต้นในโค้ดให้ */
export function getNbaSettings() {
  return get(`${ROOT}/settings/next-best-action`);
}

/**
 * Q6 — บันทึกค่าตั้งของแท็บ Next best action
 *
 * ⚠️ path มี /save ต่อท้ายโดยตั้งใจ ไม่ใช่ PUT ทับ path เดิม — ทะเบียนสิทธิ์ฝั่ง backend
 *    (APIAuthen) เทียบเฉพาะ path ไม่ดู HTTP method · การแยก path คือวิธีเดียวที่ทำให้
 *    "อ่านได้ทุก role แต่บันทึกได้เฉพาะ admin" เป็นจริง
 */
export function saveNbaSettings(settings) {
  return post(`${ROOT}/settings/next-best-action/save`, settings);
}

/**
 * ค่าตั้งของแท็บ "อารมณ์ในบทสนทนา" · ไม่มีแถวในฐาน = backend คืนค่าตั้งต้นในโค้ดให้
 * (ซึ่ง enabled = false — ฟีเจอร์นี้ต้องไปเปิดเองเสมอ)
 */
export function getSentimentSettings() {
  return get(`${ROOT}/settings/sentiment`);
}

/**
 * บันทึกค่าตั้งอารมณ์ในบทสนทนา
 *
 * ⚠️ path มี /save ต่อท้ายโดยตั้งใจ ไม่ใช่ PUT ทับ path เดิม — ทะเบียนสิทธิ์ฝั่ง backend
 *    (APIAuthen) เทียบเฉพาะ path ไม่ดู HTTP method
 */
export function saveSentimentSettings(settings) {
  return post(`${ROOT}/settings/sentiment/save`, settings);
}

/**
 * ทดสอบว่าบริการวิเคราะห์อารมณ์จากเสียง (โหมด GPU) รับสายอยู่ไหม
 *
 * ⚠️ **ไม่มีพารามิเตอร์คีย์ และห้ามเพิ่ม** — server หยิบคีย์จาก configuration ของตัวเอง
 *    ถ้าหน้าจอส่งคีย์ได้เมื่อไหร่ แปลว่าหน้าจอต้องถือคีย์ ซึ่งก็คือคีย์อยู่ใน devtools
 *    ของทุกคนที่เปิดหน้านั้น
 *
 * ⚠️ สิทธิ์ของ path นี้เท่ากับ /save (role 0/1) ไม่ใช่เท่ากับ path อ่าน — เส้นนี้สั่งให้
 *    server ยิงออกไปข้างนอกตามที่อยู่ที่ผู้เรียกส่งมา
 *
 * @param {string} [endpoint] ว่าง = ใช้ที่อยู่ที่บันทึกไว้แล้ว
 */
export async function testSentimentGpu(endpoint) {
  const res = await post(`${ROOT}/settings/sentiment/gpu/test`, { endpoint: endpoint || '' });

  if (!res || typeof res !== 'object') {
    return { outcome: GPU_PROBE_OUTCOME.BAD_RESPONSE, detail: 'API ตอบกลับมาโดยไม่มีผลการทดสอบ', ready: null };
  }

  return {
    ...res,
    outcome: toGpuProbeOutcome(res.outcome),
    detail: res.detail || '',
    ready: res.ready || null,
  };
}

/** ผลการทดสอบบริการวิเคราะห์อารมณ์จากเสียง — ชื่อที่หน้าจอใช้เทียบ */
export const GPU_PROBE_OUTCOME = {
  READY: 'ready',
  NOT_CONFIGURED: 'not-configured',
  INVALID_ENDPOINT: 'invalid-endpoint',
  UNREACHABLE: 'unreachable',
  LOADING: 'loading',
  HTTP_ERROR: 'http-error',
  BAD_RESPONSE: 'bad-response',
};

/**
 * ⚠️ กับดักเดียวกับ MIDDLEWARE_OUTCOME ข้างล่าง — `outcome` เดินทางมาเป็น **ตัวเลข**
 *    เพราะฝั่ง server ไม่ได้ลงทะเบียน JsonStringEnumConverter · ลำดับข้างล่างต้องตรงกับ
 *    enum `QaSentimentGpuProbeOutcome` เป๊ะ ห้ามสลับ ห้ามแทรกกลาง
 *    แทรกเมื่อไหร่ "โมเดลกำลังโหลด" จะกลายเป็น "ต่อไม่ติด" บนหน้าจอโดยไม่มี error ให้ใครเห็น
 */
const GPU_PROBE_BY_ORDINAL = [
  GPU_PROBE_OUTCOME.READY,
  GPU_PROBE_OUTCOME.NOT_CONFIGURED,
  GPU_PROBE_OUTCOME.INVALID_ENDPOINT,
  GPU_PROBE_OUTCOME.UNREACHABLE,
  GPU_PROBE_OUTCOME.LOADING,
  GPU_PROBE_OUTCOME.HTTP_ERROR,
  GPU_PROBE_OUTCOME.BAD_RESPONSE,
];

/** รูปสตริง — เผื่อวันหนึ่งมีคนเพิ่ม JsonStringEnumConverter ฝั่ง server จะได้ไม่พังเงียบ ๆ */
const GPU_PROBE_BY_NAME = {
  ready: GPU_PROBE_OUTCOME.READY,
  notconfigured: GPU_PROBE_OUTCOME.NOT_CONFIGURED,
  invalidendpoint: GPU_PROBE_OUTCOME.INVALID_ENDPOINT,
  unreachable: GPU_PROBE_OUTCOME.UNREACHABLE,
  loading: GPU_PROBE_OUTCOME.LOADING,
  httperror: GPU_PROBE_OUTCOME.HTTP_ERROR,
  badresponse: GPU_PROBE_OUTCOME.BAD_RESPONSE,
};

function toGpuProbeOutcome(raw) {
  if (typeof raw === 'number') return GPU_PROBE_BY_ORDINAL[raw] || GPU_PROBE_OUTCOME.BAD_RESPONSE;
  const key = String(raw ?? '').toLowerCase().replace(/[^a-z]/g, '');
  return GPU_PROBE_BY_NAME[key] || GPU_PROBE_OUTCOME.BAD_RESPONSE;
}

/** ค่าตั้งการจัดสายงานตอนปิดบอท (Q7) */
export function getPrechatRouting() {
  return get(`${ROOT}/settings/prechat-routing`);
}

/**
 * บันทึกค่าตั้ง Q7
 *
 * ⚠️ path ต่างจาก endpoint อ่าน (มี /save ต่อท้าย) เพราะฝั่ง server เทียบสิทธิ์ด้วย path
 *    อย่างเดียว ไม่ดู HTTP method — ห้ามรวบเป็น path เดียว
 */
export function savePrechatRouting(settings) {
  return post(`${ROOT}/settings/prechat-routing/save`, settings);
}

/** ทะเบียนกลาง "เหตุผลที่เจ้าหน้าที่กด unlike" 3 ภาษา (M) */
export function getFeedbackReasons() {
  return get(`${ROOT}/settings/feedback-reasons`);
}

/**
 * บันทึกทะเบียนเหตุผล
 *
 * ⚠️ path มี /save ต่อท้ายด้วยเหตุผลเดียวกับสองแท็บก่อนหน้า — ฝั่ง server เทียบสิทธิ์
 *    ด้วย path อย่างเดียว ไม่ดู HTTP method
 */
export function saveFeedbackReasons(settings) {
  return post(`${ROOT}/settings/feedback-reasons/save`, settings);
}

/** ทะเบียน agent พร้อมจำนวนเคส */
export function getAgents({ from = null, to = null } = {}) {
  const qs = [];
  if (from) qs.push(`from=${encodeURIComponent(from)}`);
  if (to) qs.push(`to=${encodeURIComponent(to)}`);
  return get(`${ROOT}/agents${qs.length ? `?${qs.join('&')}` : ''}`);
}

/* ==========================================================================
   Q9 — ให้คะแนน QA ของจริง: ค่าตั้ง 5 แท็บ + ผลประเมินบนหน้าเคส

   ⚠️ ค่าตั้งกลุ่มนี้ **สั่งงานระบบจริง** (rubric กำหนดว่าเจ้าหน้าที่ถูกวัดด้วยหัวข้ออะไร ·
      policy ขีดเส้นผ่าน/ตกที่มีผลย้อนหลังทั้งระบบ · evaluator เปิด/ปิดการยิง LLM
      ที่มีค่าใช้จ่ายต่อทุกเคสที่ปิด) — เคยมีชั้นจำลองที่บันทึกลง localStorage แล้วขึ้นว่า
      สำเร็จทั้งที่ worker ไม่เคยเห็นค่า · Q10 ลบชั้นนั้นทิ้ง เส้นทางเดียวคือฐานข้อมูลจริง
   ========================================================================== */

/**
 * Q9 — rubric ของช่องทาง (chat | voice) พร้อมสถานะฉบับร่าง
 *
 * ไม่มีแถวในฐาน = backend คืน rubric ตั้งต้นในโค้ดเป็น active v1 (`isDefault: true`)
 * — หน้าจอจึงเปิดได้เสมอแม้ยังไม่เคยมีใครบันทึกอะไร
 */
export function getRubric(channel) {
  return get(`${ROOT}/settings/rubric/${encodeURIComponent(channel)}`);
}

/**
 * Q9 — บันทึก rubric เป็น**ฉบับร่าง** (ไม่บังคับผลรวม 100 · worker ยังใช้ active ตัวเดิม)
 *
 * ⚠️ path มี /save ต่อท้ายโดยตั้งใจ — ทะเบียนสิทธิ์ฝั่ง backend (APIAuthen) เทียบเฉพาะ
 *    path ไม่ดู HTTP method · การแยก path คือวิธีเดียวที่แยก "อ่าน" ออกจาก "เขียน" ได้
 */
export function saveRubric(channel, rubric) {
  // ⚠️ ส่งเฉพาะรูปของ "เอกสาร rubric" — ห้ามส่ง view object ทั้งก้อน · view มี version เป็น
  //    สตริง ("v1") แต่ฝั่ง server bind เข้า int → 400 ทุกครั้ง (server เป็นเจ้าของ version เองอยู่แล้ว)
  const doc = rubric ? { name: rubric.name, parts: rubric.parts, gates: rubric.gates } : {};
  return post(`${ROOT}/settings/rubric/${encodeURIComponent(channel)}/save`, doc);
}

/**
 * Q9 — publish ฉบับร่างให้เป็น active (role 0 เท่านั้น — รูปย่อของ maker-checker)
 *
 * ⚠️ validation ไม่ผ่าน (รวมไม่ครบ 100 ฯลฯ) backend ตอบ 400 พร้อมข้อความไทย
 *    ใน `{message}` — ผู้เรียกโชว์ `err.message` ได้ตรง ๆ ไม่ต้องแปลเอง
 */
export function publishRubric(channel) {
  return post(`${ROOT}/settings/rubric/${encodeURIComponent(channel)}/publish`);
}

/**
 * Q9 — "ทดลองให้คะแนน" 1 หัวข้อกับเคสที่ปิดแล้ว
 *
 * ⚠️ เสีย 1 LLM call จริงต่อการกด — ไม่ใช่ preview ฟรี · ผู้เรียกควรกันกดรัว
 * @param {string} channel chat | voice
 * @param {{caseKey:string, criteria:object}} payload หัวข้อที่จะลอง + เลขเคส (เช่น C42)
 */
export function testCriteriaPrompt(channel, payload) {
  return post(`${ROOT}/settings/rubric/${encodeURIComponent(channel)}/test`, payload);
}

/**
 * Q9 — เกณฑ์ผ่าน/ไม่ผ่าน · ห่อ `{ value, updatedAt, updatedBy, isCustomized }`
 * ไม่มีแถวในฐาน = ค่าตั้งต้นในโค้ด (passMark 80 · borderlineBand 5)
 */
export function getPolicy() {
  return get(`${ROOT}/settings/policy`);
}

/**
 * Q9 — บันทึกเกณฑ์ผ่าน/ไม่ผ่าน
 *
 * ⚠️ มีผลกับเคสเก่า**ย้อนหลังทันที** — ผลตัดสินคำนวณตอนอ่านจาก policy ปัจจุบัน
 *    ไม่ใช่ค่าที่แช่แข็งไว้ตอนประเมิน (design: "เป็นแค่เส้นตัด ไม่ใช่การให้คะแนนใหม่")
 */
export function savePolicy(policy) {
  return post(`${ROOT}/settings/policy/save`, policy);
}

/**
 * Q9 — "ผลกระทบย้อนหลัง" ของเกณฑ์ชุดใหม่กับเคสจริงในระบบ ก่อนกดบันทึก
 * @param {object} candidate เกณฑ์ชุดใหม่ที่อยากลอง (รูปเดียวกับ value ของ getPolicy)
 * @returns {Promise<{total:number, pass:number, borderline:number, fail:number, changedCount:number}>}
 */
export function getPolicyImpact(candidate) {
  return post(`${ROOT}/settings/policy/impact`, candidate);
}

/**
 * Q9 — ค่าตั้ง AI Evaluator · ไม่มีแถวในฐาน = ค่าตั้งต้นในโค้ด
 * (ซึ่ง enabled = false — ฟีเจอร์นี้ต้องไปเปิดเองเสมอ แบบเดียวกับ sentiment)
 */
export function getEvaluatorConfig() {
  return get(`${ROOT}/settings/evaluator`);
}

/**
 * Q9 — บันทึกค่าตั้ง AI Evaluator
 *
 * ⚠️ สวิตช์ enabled ในนี้เปิด/ปิดการยิง LLM ต่อ**ทุกเคสที่ปิด** — ค่าตั้งนี้มีราคา
 *    เป็นเงินจริงต่อรอบ จึงต้องลงฐานข้อมูลให้ worker เห็น ไม่ใช่ localStorage
 */
export function saveEvaluatorConfig(config) {
  return post(`${ROOT}/settings/evaluator/save`, config);
}

/** Q11 — ค่าตั้งงานสรุปการสนทนา (section 'summary') */
export function getSummaryConfig() {
  return get(`${ROOT}/settings/summary`);
}

/**
 * Q11 — บันทึกค่าตั้งงานสรุปการสนทนา
 *
 * ⚠️ สวิตช์ enabled ในนี้ **ตั้งต้นเปิด** ต่างจาก AI Evaluator — ปิดแล้วฟอร์มปิดงาน
 *    ของเจ้าหน้าที่ (callSummary บน Salesforce) จะว่างทุกเคสตั้งแต่รอบถัดไป
 */
export function saveSummaryConfig(config) {
  return post(`${ROOT}/settings/summary/save`, config);
}

/**
 * V1 — ค่าตั้งเส้นเสียง Voxtron (WS stream ของ Fusion.Middleware)
 *
 * ⚠️ ผู้อ่านค่าคือ middleware ไม่ใช่ CF — middleware อ่านตอน**เปิด socket ของแต่ละสาย**
 *    ค่าที่แก้จึงมีผลกับสายถัดไป ไม่ใช่สายที่กำลังคุยอยู่
 */
export function getVoiceStreamConfig() {
  return get(`${ROOT}/settings/voice-stream`);
}

/** V1 — บันทึกค่าตั้งเส้นเสียง Voxtron */
export function saveVoiceStreamConfig(config) {
  return post(`${ROOT}/settings/voice-stream/save`, config);
}

/**
 * V1 — ทดสอบถอดเสียง 1 คลิป (การ์ด "ทดสอบถอดเสียง") · CF ส่งต่อไป Fusion.Middleware /api/mw/v1/stt/test
 * payload: { audioBase64 (PCM16 16 kHz mono หรือ WAV · ≤ 15 วิ), format: 'pcm16'|'wav', provider: 'azure'|'own'|'both', settings }
 * คืน { outcome, httpStatus, message, result: { provider, audio_ms, total_ms, azure, own }, settingsIgnored }
 * ⚠️ ความล้มเหลวที่คาดไว้ (middleware ไม่ได้ตั้ง/ต่อไม่ติด/เกินเพดาน) มาเป็น outcome ≠ 'ok' ไม่ใช่ exception
 */
export function testVoiceStream(payload) {
  return post(`${ROOT}/settings/voice-stream/test`, payload);
}

/** Q9 — ค่าตั้งการคัดเคสเข้าประเมิน (coverage / sampling / เงื่อนไขข้าม) */
export function getPipelineConfig() {
  return get(`${ROOT}/settings/pipeline`);
}

/** Q9 — บันทึกค่าตั้งการคัดเคสเข้าประเมิน */
export function savePipelineConfig(config) {
  return post(`${ROOT}/settings/pipeline/save`, config);
}

/**
 * Q9 — กฎการปกปิดข้อมูลส่วนบุคคลก่อนส่งเข้า LLM
 * คืน `{ value: { rules, customPatterns }, catalog, ... }` — catalog คือทะเบียนกฎ
 * ทั้งหมดฝั่ง backend (ป้าย/รูปแบบ/บังคับไหม) ให้หน้าจอวาดสวิตช์โดยไม่ฮาร์ดโค้ด
 */
export function getMaskingRules() {
  return get(`${ROOT}/settings/masking`);
}

/**
 * Q9 — บันทึกกฎการปกปิด
 *
 * ⚠️ custom regex ที่ compile ไม่ผ่าน/อันตราย backend ตอบ 400 พร้อมข้อความไทยใน
 *    `{message}` — ต้องปฏิเสธที่ server เพราะ regex พังตัวเดียวทำให้ worker ข้ามการ mask
 */
export function saveMaskingRules(settings) {
  return post(`${ROOT}/settings/masking/save`, settings);
}

/**
 * Q9 — ลองกฎปกปิดกับข้อความตัวอย่าง (before/after จริงจาก masker ตัวเดียวกับ worker)
 * @param {{text:string, settings:object}} payload
 * @returns {Promise<{original:string, masked:string}>}
 */
export function previewMasking(payload) {
  return post(`${ROOT}/settings/masking/preview`, payload);
}

/**
 * Q9 — ผลประเมินของเคส (revision ปัจจุบัน) — คะแนนรายหมวด · gates · บทวิเคราะห์ ·
 * เหตุผลที่หัก · บรรทัดที่อ้างเป็นหลักฐาน (`flaggedLineIds`)
 *
 * ⚠️ สิทธิ์แคบกว่าหัวเคส (role 0,1 เท่านั้น — role 2 ได้ 401/403) เพราะพ่วง
 *    evidence quote จากบทสนทนาจริง · ผู้เรียกต้องกลืน error นี้แบบเดียวกับ transcript
 *    ไม่ใช่ปล่อยให้ทั้งหน้าพัง · 404 = ไม่พบเคส (ไม่ใช่ "ยังไม่ประเมิน" — กรณีนั้น
 *    ได้ 200 พร้อม evalStatus: pending|not-evaluated)
 */
export function getCaseEvaluation(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/evaluation`);
}

/**
 * Q9 — สั่งประเมินใหม่ (role 0,1) — เข้าคิวเป็น revision ใหม่ ของเก่าไม่หาย
 * (คะแนนเดิมคือสิ่งที่เจ้าหน้าที่โต้แย้ง — ห้ามลบทับ)
 * @returns {Promise<{caseNo:string, queued:boolean, reason:string}>}
 */
export function requestEvaluation(caseKey) {
  return post(`${ROOT}/cases/${encodeURIComponent(caseKey)}/evaluate`);
}

/**
 * Q9 — เส้นอารมณ์ของเคสจาก cc.QaSentimentRuns (สเกล 0-100 แปลงจาก −1..+1 ให้แล้ว)
 *
 * data เป็น null ได้เป็นเรื่องปกติ — เคสก่อนเปิดฟีเจอร์ sentiment หรือยังไม่ถึงรอบแรก
 * ผู้เรียกต้องถือ null เป็น "ไม่มีข้อมูล" ไม่ใช่ error
 */
export function getCaseSentiment(caseKey) {
  return get(`${ROOT}/cases/${encodeURIComponent(caseKey)}/sentiment`);
}

/* ==========================================================================
   Q10 — สถิติรวมของ 4 หน้า (Overview · Agents · Agent detail · Investigations)

   ⚠️ ตัวเลขทุกตัวคำนวณตอนอ่านฝั่ง server ด้วยสูตรเดียว (`QaVerdict`) — ห้ามคิดซ้ำใน JS
      มีสองสูตรเมื่อไหร่ KPI บนหน้า Overview กับจำนวนแถวที่ตัวกรองบน Case Explorer
      ให้มา จะเถียงกันเองโดยไม่มีใครรู้ว่าฝั่งไหนผิด
   ⚠️ ทุก response มี `truncated` · true = ชนเพดาน 20,000 เคส ตัวเลขคิดจากเท่าที่ดึงมา
      หน้าจอต้องขึ้นป้ายเสมอ ห้ามกลืน — KPI ที่ตัดข้อมูลเงียบ ๆ คือเลขผิดที่ไม่มีใครจับได้
   ⚠️ `caseCount`/`avgScore`/`passRate` นับเฉพาะเคสที่**ประเมินสำเร็จ** · ที่เหลืออยู่ใน
      `notEvaluated` แยกช่อง — อย่ารวมสองก้อนเข้าด้วยกันเพื่อให้ดูเหมือนครอบคลุม 100%
   ========================================================================== */

/**
 * KPI + leaderboard + เคสที่ต้องรีวิว ของหน้า Overview
 * @param {{channel?:string, topic?:string, range?:number}} filter ตัวกรองร่วมจาก Vuex
 */
export async function getOverview(filter) {
  const res = await get(`${ROOT}/analytics/overview${toQueryString(toAnalyticsQuery(filter))}`);
  return { ...res, leaderboard: ((res && res.leaderboard) || []).map(withAgentAvatar) };
}

/**
 * ตารางผลงานรายเจ้าหน้าที่ (หน้า QAAgents)
 *
 * ⚠️ ตั้งชื่อต่างจาก `getAgents` โดยตั้งใจ — `getAgents` คือ**ทะเบียน** agent (ชื่อ/บทบาท/
 *    จำนวนเคส) ที่ dropdown ใช้ · ตัวนี้คือ**คะแนน** ซึ่งคนละ endpoint คนละรูป
 *    ทับชื่อกันเมื่อไหร่ หน้าใดหน้าหนึ่งจะอ่าน field ไม่เจอแบบเงียบ ๆ
 */
export async function getAgentScorecard(filter) {
  const res = await get(`${ROOT}/analytics/agents${toQueryString(toAnalyticsQuery(filter))}`);
  return { ...res, items: ((res && res.items) || []).map(withAgentAvatar) };
}

/**
 * หน้าเจ้าหน้าที่รายคน — hero + เส้นแนวโน้ม + ค่าเฉลี่ยรายหัวข้อ + เคสล่าสุด
 *
 * ⚠️ `agentKey` คือคีย์จริงจาก `cc.QaAgents` ที่ leaderboard/ตารางส่งต่อมา
 *    · agent ที่ไม่มีเคสในช่วงที่เลือก server ตอบ 200 พร้อมผลว่าง ไม่ใช่ 404
 *      (หน้าจอจึงต้องเล่า "ไม่มีเคสในช่วงนี้" ไม่ใช่ขึ้นหน้าแดงว่าหาคนไม่เจอ)
 */
export async function getAgentDetail(agentKey, filter) {
  const res = await get(
    `${ROOT}/analytics/agents/${encodeURIComponent(agentKey)}${toQueryString(toAnalyticsQuery(filter))}`
  );
  return { ...res, agent: withAgentAvatar(res && res.agent) };
}

/**
 * รายการเคสที่ต้องสอบสวน (verdict ≠ pass) + สรุป 3 ก้อน
 * @param {object} filter ตัวกรองร่วม
 * @param {'all'|'gate'|'low'} bucket ปุ่มกรองบนหน้า · 'all' = ไม่ส่งคีย์ (ค่าตั้งต้นฝั่ง server)
 */
export function getInvestigations(filter, bucket) {
  const params = toAnalyticsQuery(filter);
  if (bucket && bucket !== 'all') params.bucket = bucket;
  return get(`${ROOT}/analytics/investigations${toQueryString(params)}`);
}

/**
 * สารบัญหัวข้อของจริง (`QaTopicCatalog` จาก `sy.Dbsettings` คีย์ `QaTopicTaxonomy`)
 *
 * ⚠️ ค่าที่ใช้กรองคือ `topicId` — ตรงกับคอลัมน์ `QaCases.TopicId` ที่ตัวจัดหมวดเขียนไว้
 *    ของเดิม dropdown หยิบ `categoryId` จากสารบัญจำลองซึ่งเป็นคนละแกน เลือกหัวข้อไหน
 *    ก็ได้ 0 แถวเสมอ · `isDefault: true` = ยังไม่เคยมีใครตั้งสารบัญ ใช้ค่าในโค้ดอยู่
 */
export function getTopics() {
  return get(`${ROOT}/topics`);
}

/* ==========================================================================
   Fusion.Middleware — สถานะงานเบื้องหลังทั้งหก
   (classification · recommend · nba · sentiment · evaluation · summary)

   งานหกตัวนี้**ไม่ได้อยู่ในรีโปนี้แล้ว** — ย้ายไป service แยกชื่อ Fusion.Middleware
   ซึ่งใช้ฐานข้อมูลตัวเดียวกันแต่ไม่มีหน้าเว็บของตัวเองโดยตั้งใจ · หน้า QAJobs ฝั่งนี้
   จึงเป็น "ตา" เดียวที่ทีมมีไว้ดูว่า worker ยังเดินอยู่จริงไหม

   🔒 ⚠️ Api-Key ของ Fusion.Middleware **ห้ามเดินทางมาถึงเบราว์เซอร์เด็ดขาด**
      หน้าเว็บยิงมาที่ `api/v1/QaConsole/middleware/jobs` ของ Chat Framework ด้วย
      คุกกี้ล็อกอินของระบบเองตามปกติ แล้ว Chat Framework เป็นฝ่ายถือคีย์ไปคุยกับ
      middleware ฝั่ง server · ห้ามเพิ่มเส้นทางที่หน้าเว็บอ่านค่า BaseUrl/ApiKey
      แล้วยิงตรงไปที่ middleware ไม่ว่าจะด้วยเหตุผลว่า "ง่ายกว่า" แค่ไหนก็ตาม —
      นั่นคือการเอาคีย์ของ service ที่คุมงานเบื้องหลังทั้งหมดไปแปะไว้ใน devtools
      ของทุกคนที่เปิดหน้านี้ได้
   ========================================================================== */

/**
 * ผลของการที่ Chat Framework ไปถาม Fusion.Middleware — คู่กับ enum
 * `QaMiddlewareStatusOutcome` ฝั่ง C# แต่เป็นสตริงเพื่อให้หน้าจออ่านออกโดยไม่ต้องจำเลข
 *
 * ⚠️ ห้ามยุบเป็น "error" ก้อนเดียว — หกค่านี้คือหกการกระทำที่ต่างกันสิ้นเชิงของคนที่ต้องแก้
 *    (ยังไม่ได้ตั้งค่า / ต่อไม่ติด / คีย์ผิด / ฝั่งโน้นตอบไม่ใช่ 200 / สัญญาไม่ตรง / ไม่รู้)
 */
export const MIDDLEWARE_OUTCOME = {
  OK: 'ok',
  NOT_CONFIGURED: 'not-configured',
  UNREACHABLE: 'unreachable',
  UNAUTHORIZED: 'unauthorized',
  HTTP_ERROR: 'http-error',
  BAD_RESPONSE: 'bad-response',
  UNEXPECTED: 'unexpected',
};

/**
 * ⚠️ ค่า `outcome` เดินทางมาเป็น **ตัวเลข** ไม่ใช่ชื่อ — ฝั่ง server ใช้ System.Text.Json
 *    ค่าเริ่มต้น (`services.AddMvc()` ไม่ได้ลงทะเบียน JsonStringEnumConverter ไว้)
 *    enum จึงกลายเป็น ordinal ตอน serialize · ลำดับข้างล่างต้องตรงกับ
 *    `QaMiddlewareStatusOutcome` เป๊ะ — ห้ามสลับ ห้ามแทรกกลาง เพราะแทรกเมื่อไหร่
 *    "คีย์ผิด" จะกลายเป็น "ต่อไม่ติด" บนหน้าจอโดยไม่มี error ให้ใครเห็น
 */
const OUTCOME_BY_ORDINAL = [
  MIDDLEWARE_OUTCOME.OK,
  MIDDLEWARE_OUTCOME.NOT_CONFIGURED,
  MIDDLEWARE_OUTCOME.UNREACHABLE,
  MIDDLEWARE_OUTCOME.UNAUTHORIZED,
  MIDDLEWARE_OUTCOME.HTTP_ERROR,
  MIDDLEWARE_OUTCOME.BAD_RESPONSE,
  MIDDLEWARE_OUTCOME.UNEXPECTED,
];

/** รูปสตริง — เผื่อวันหนึ่งมีคนเพิ่ม JsonStringEnumConverter ฝั่ง server จะได้ไม่พังเงียบ ๆ */
const OUTCOME_BY_NAME = {
  ok: MIDDLEWARE_OUTCOME.OK,
  notconfigured: MIDDLEWARE_OUTCOME.NOT_CONFIGURED,
  unreachable: MIDDLEWARE_OUTCOME.UNREACHABLE,
  unauthorized: MIDDLEWARE_OUTCOME.UNAUTHORIZED,
  httperror: MIDDLEWARE_OUTCOME.HTTP_ERROR,
  badresponse: MIDDLEWARE_OUTCOME.BAD_RESPONSE,
  unexpected: MIDDLEWARE_OUTCOME.UNEXPECTED,
};

function toMiddlewareOutcome(raw) {
  if (typeof raw === 'number') return OUTCOME_BY_ORDINAL[raw] || MIDDLEWARE_OUTCOME.UNEXPECTED;
  const key = String(raw ?? '')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return OUTCOME_BY_NAME[key] || MIDDLEWARE_OUTCOME.UNEXPECTED;
}

/**
 * สถานะงานเบื้องหลังทั้งหกของ Fusion.Middleware · อ่านอย่างเดียว ไม่สั่งอะไรทั้งสิ้น
 *
 * ⚠️ สิทธิ์แคบ (role 0,1 ตามทะเบียนใน APIAuthen) — ผู้เรียกต้องรับ 401/403 ได้
 *
 * ⚠️ **ไม่แบน `status` ออกมาเป็นช่องระดับบนโดยตั้งใจ** · ถ้าแบนแล้วเติมค่าเริ่มต้นให้
 *    (`jobsEnabled: false` ตอนอ่านไม่ได้) หน้าจอจะแยกไม่ออกระหว่าง "สวิตช์ใหญ่ปิดอยู่"
 *    กับ "เราอ่านสถานะไม่ได้เลย" ซึ่งเป็นคนละเรื่องกันคนละโลก และเป็นความเข้าใจผิด
 *    ที่แพงที่สุดของหน้านี้ — คนจะไปตามเปิดสวิตช์ที่เปิดอยู่แล้ว
 *    `status` จึงเป็น null เสมอเมื่อ `isOk` เป็น false และหน้าจอต้องเช็ค `isOk` ก่อนแตะ
 *
 * ⚠️ ไม่เรียง `jobs` ใหม่ — ส่งต่อตามลำดับที่ได้รับมาจริง (ฝั่งโน้นสัญญาว่าเรียงคงที่)
 *    การเรียงเองจะกลบกรณีที่ฝั่งโน้นเปลี่ยนสัญญา ซึ่งเป็นสิ่งที่ต้องเห็น ไม่ใช่ซ่อน
 *
 * @returns {Promise<{outcome:string, isOk:boolean, httpStatusCode:number|null,
 *                    detail:string, endpoint:string, status:object|null, jobs:object[]}>}
 */
export async function getMiddlewareJobs() {
  const res = await get(`${ROOT}/middleware/jobs`);

  // ตามสัญญา `data` ไม่มีทางเป็น null (client ฝั่ง server คืน result เสมอ ไม่โยน)
  // แต่ถ้าเป็นจริงขึ้นมา ต้องอ่านว่า "ตอบมาแต่อ่านไม่ออก" ไม่ใช่ปล่อยให้หน้าจอพัง
  if (!res || typeof res !== 'object') {
    return {
      outcome: MIDDLEWARE_OUTCOME.BAD_RESPONSE,
      isOk: false,
      httpStatusCode: null,
      detail: 'API ตอบกลับมาโดยไม่มีข้อมูลสถานะ',
      endpoint: '',
      status: null,
      jobs: [],
    };
  }

  const outcome = toMiddlewareOutcome(res.outcome);

  // ⚠️ คิด isOk เองด้วยกฎเดียวกับฝั่ง C# แทนที่จะเชื่อ `res.isOk` อย่างเดียว —
  //    ช่องนั้นเป็น property แบบ get อย่างเดียว ถ้าวันหนึ่งมีคนตั้งค่า serializer
  //    ให้ข้าม property ที่ไม่มี setter หน้านี้จะกลายเป็น "อ่านไม่ได้" ตลอดกาลแบบเงียบ ๆ
  const status = outcome === MIDDLEWARE_OUTCOME.OK && res.status ? res.status : null;

  return {
    outcome,
    isOk: !!status,
    httpStatusCode: res.httpStatusCode ?? null,
    detail: res.detail || '',
    endpoint: res.endpoint || '',
    status,
    jobs: (status && Array.isArray(status.jobs) ? status.jobs : []),
  };
}

/* ==========================================================================
   ตัวช่วยหน้าจอ — ย้ายมาจากชั้นผสมที่ถูกลบทิ้งใน Q10
   ========================================================================== */

const AV_COLORS = ['#0E7C6B', '#3a4bd1', '#C98A14', '#7a4bd1', '#0d7ba8', '#c0453b', '#2E7D5B', '#b5476e'];

/** hash คงที่ — สีอวาตาร์/ป้ายหัวข้อต้องเหมือนเดิมทุกครั้งที่เปิด ไม่ใช่สุ่มใหม่ต่อ render */
function hashOf(text) {
  let h = 2166136261;
  for (let i = 0; i < String(text).length; i += 1) {
    h ^= String(text).charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

function initialsOf(name) {
  if (!name) return '?';
  return String(name)
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('');
}

function colorOf(key) {
  return AV_COLORS[hashOf(key || '?') % AV_COLORS.length];
}

/**
 * ติดอวาตาร์ให้แถวเจ้าหน้าที่ที่มาจาก endpoint analytics
 *
 * ⚠️ `QaAgentScorecardItem` ของ server **ไม่มี** `initials`/`color` — มีแต่ตัวเลขผลงาน
 *    · 3 หน้าที่ใช้แถวนี้ (Overview leaderboard · QAAgents · QAAgentDetail hero) อ่าน
 *      สองช่องนี้ผ่าน `QaAgentCell` อยู่ ซึ่งเคยได้ค่าจากชั้นข้อมูลจำลองที่ถูกลบใน Q10
 *      ปล่อยไว้จะได้วงกลมเปล่าไม่มีตัวอักษร — ไม่ error ไม่มีอะไรจับได้
 *
 * ⚠️ ต้องปั้นที่ชั้น service เท่านั้น ห้ามให้แต่ละหน้าคิดสีเอง · สีผูกกับ `agentId`
 *    ซึ่งคือ `AgentKey` ตัวเดียวกับที่ `toListItem`/`getLiveAgents` ใช้ คนเดียวกันจึงได้
 *    สีเดิมทุกหน้า · ถ้าแยกไปคิดในหน้าจอ คนเดียวกันจะเปลี่ยนสีตามหน้าที่เปิด
 */
function withAgentAvatar(agent) {
  if (!agent) return agent;
  return { ...agent, initials: initialsOf(agent.agentName), color: colorOf(agent.agentId) };
}

/**
 * ข้อความแทนช่องว่าง — ต้องแยก "ยังไม่ได้จัด" ออกจาก "จัดแล้วไม่เข้าหมวด"
 *
 * ⚠️ ถ้าใช้ "—" เหมือนกันหมด คนดูจะแยกไม่ออกว่า AI ยังไม่ได้ทำ หรือทำแล้วแต่จัดไม่ได้
 *    ซึ่งนำไปสู่การไล่ปัญหาผิดทาง (ไปดู log ของโมเดล ทั้งที่ worker ยังไม่ได้แตะเคสนั้น)
 */
function pendingLabel(row) {
  if (!row.classifyStatus) return 'รอจัดหมวด';
  if (row.classifyStatus === 'failed') return 'จัดหมวดไม่สำเร็จ';
  return '—';
}

/** สีของป้ายหัวข้อ — คงที่ต่อหัวข้อ และจางลงเมื่อยังไม่ได้จัด */
function topicPalette(topicId) {
  if (!topicId) return { topicColor: '#64748b', topicBg: '#f1f5f9' };
  const hue = hashOf(topicId) % 360;
  return { topicColor: `hsl(${hue} 55% 30%)`, topicBg: `hsl(${hue} 70% 94%)` };
}

/**
 * แปลงเคสจริงให้มีหน้าตาแบบเดียวกับที่ตารางเดิมคาดหวัง
 *
 * Q9 — คะแนน/สถานะมาจากคอลัมน์จริงของ API ทุกช่องแล้ว (server คำนวณ qaStatus จาก
 * policy ปัจจุบันตอนอ่าน — สูตรอยู่ที่ QaVerdict ฝั่ง C# ที่เดียว ห้ามคำนวณซ้ำใน JS)
 */
function toListItem(row) {
  return {
    caseNo: row.caseNo,
    conversationId: row.conversationId,
    evaluatedAt: row.startedAt,
    // ⚠️ เคยฮาร์ดโค้ดเป็น 'chat' ตรงนี้ · ตอนนี้อ่านจากคอลัมน์ CaseType ของจริงแล้ว
    //    (ค่าที่ได้ยังเป็น chat ทั้งหมดอยู่ดี แต่พอ ingestion ของเสียงเข้ามาจะถูกทันที
    //     โดยไม่ต้องกลับมาแก้หน้าจอ)
    channel: row.caseType || 'chat',
    channelId: row.channelId,
    // ⚠️ หัวข้อมาจาก AI ที่ดูข้อความแรกของลูกค้า **ไม่ใช่** ServiceType ของ Salesforce
    //    ServiceType เป็นสายงานสำหรับ routing (มีแค่ Banking/CreditCard และต้องเปิด Prechat)
    //    ซึ่งหยาบเกินกว่าจะเป็นหัวข้อ และส่วนใหญ่เป็น null
    topicId: row.topicId || 'unclassified',
    topicLabel: row.topicLabel || pendingLabel(row),
    ...topicPalette(row.topicId),
    // ⚠️ สายงานสำหรับ routing (Banking / CreditCard) — แสดง**คู่กับ**หัวข้อ ไม่ใช่แทนที่
    //    ว่างได้และว่างบ่อย: เคสที่เปิดตอนบอทคัดแยกปิดอยู่ หรือเคส stub ที่เกิดจาก
    //    event ของ SSE มาก่อน จะไม่มีค่านี้เลย · ฝั่งหน้าจอจึงต้องซ่อน chip เมื่อว่าง
    //    ไม่ใช่โชว์ค่าเริ่มต้นมั่ว ๆ ซึ่งจะทำให้คนอ่านคิดว่าเคสนั้นถูกจัดสายงานแล้ว
    serviceType: row.serviceType || '',
    // R2 — false = ยังเป็นการเดาจากข้อความแรก · true = สรุปจากบทสนทนาทั้งใบแล้ว
    // ⚠️ ต้องแยกให้เห็นบนหน้าจอ ไม่งั้นคนอ่านจะเชื่อค่าที่ยังเดาอยู่เท่ากับค่าที่สรุปแล้ว
    isFinalClassification: !!row.isFinalClassification,
    // ⚠️ ห้ามถอยไปใช้ชื่อลูกค้าเป็น "เรื่องที่ติดต่อ" — เคยทำแล้วชื่อลูกค้าจาก LINE
    //    ไปโผล่ผิดคอลัมน์ ซึ่งทั้งอ่านไม่รู้เรื่องและเป็นข้อมูลส่วนบุคคลที่ไม่ควรอยู่ตรงนั้น
    reason: row.caseReason || pendingLabel(row),
    classifyStatus: row.classifyStatus,
    agentId: row.agentKey || '',
    agentName: row.agentName || 'ยังไม่มีเจ้าหน้าที่รับ',
    agentInitials: initialsOf(row.agentName),
    agentColor: colorOf(row.agentKey || row.caseNo),
    durationSec: row.durationSec,
    agentJoinCount: row.agentJoinCount,
    caseStatus: row.status,
    customerName: row.customerName,

    // ── Q9 — ผลประเมินจริงจากคอลัมน์ของ API ─────────────────────────────────
    // totalScore ปัดตาม policy แล้วจากฝั่ง server · null = ยังไม่มีคะแนน (อย่าแทนด้วย 0
    // — ศูนย์คือ "ได้ศูนย์คะแนน" ซึ่งเป็นผลประเมินจริงที่แย่ที่สุด ไม่ใช่ "ยังไม่ประเมิน")
    totalScore: row.totalScore ?? null,
    // pass|borderline|fail|pending|not-evaluated — คำนวณจาก policy ปัจจุบันตอนอ่าน
    qaStatus: row.qaStatus || 'pending',
    // ⚠️ alias ให้โค้ดเก่าที่ยังอ่าน `status` เป็นผลตัดสิน — วงจรชีวิตของเคส (open|closed)
    //    อยู่ที่ `caseStatus` ข้างบน ไม่ใช่ช่องนี้ · ยังคงไว้เพราะตารางหลายตัวยังผูกชื่อนี้
    status: row.qaStatus || 'pending',
    // ok|failed|skipped|pending|not-evaluated — สถานะของ**งานประเมิน** (คนละแกนกับผลตัดสิน:
    // งานสำเร็จแต่ตก QA ได้ และงานล้มโดยไม่รู้ผล QA ก็ได้ — หน้าจอต้องเล่าแยกกัน)
    evalStatus: row.evalStatus || 'pending',
    isGateFail: !!row.isGateFail,
    hasAutoFail: !!row.hasAutoFail,
    needsHumanReview: !!row.needsHumanReview,
  };
}

/**
 * Q6 — แปลงคำแนะนำของจริงให้เป็นรูปที่ QaAssistPanel อ่านอยู่แล้ว
 *
 * ⚠️ ต้องคง key เดิม (category / action / say / why) ไว้ทุกตัว ไม่งั้น component
 *    ต้องเขียนสองทางสำหรับข้อมูลชุดเดียวกัน · ของใหม่คือ sayTh (คำแปล) กับ rating
 *
 * ⚠️ ไม่มี `used` โดยตั้งใจ — ระบบจริงไม่รู้ว่าเจ้าหน้าที่ใช้คำแนะนำนั้นหรือไม่
 *    (Salesforce ไม่ได้บอกมา) การเดาเป็น 'dismissed' จะกลายเป็นสถิติที่ผิดใน QA
 */
function toNbaCard(item) {
  return {
    nbaId: item.nbaId,
    typeId: item.typeId,
    category: item.typeLabel || item.typeId || '',
    action: item.action,
    say: item.say,
    sayTh: item.sayTh || null,
    sayLanguage: item.sayLanguage || null,
    why: item.reason,
    timestamp: item.timestamp,
    rating: item.rating || null,
    ratingReasons: item.ratingReasons || [],
  };
}

/* ==========================================================================
   รูปที่ component อ่าน — ประกอบจาก endpoint ดิบข้างบน
   ========================================================================== */

export async function getCases(query = {}) {
  const res = await searchCases(query);

  return {
    items: (res.items || []).map(toListItem),
    total: res.total,
    page: res.page,
    pageSize: res.pageSize,
  };
}

export async function getCaseDetail(caseNo) {
  const detail = await getCase(caseNo);

  // Q9 — ผลประเมินจริง (revision ปัจจุบัน) · สิทธิ์แคบกว่าหัวเคส (role 0,1) เพราะพ่วง
  // evidence quote จากบทสนทนา — role 2 ได้ 401/403 ตรงนี้ ต้องกลืนเป็น evaluationError
  // แบบเดียวกับ transcript ไม่ใช่ปล่อยให้ทั้งหน้าพัง
  //
  // ⚠️ 404 = "ไม่พบเคส" (เคสมีจริงแต่ยังไม่ประเมิน ได้ 200 + evalStatus pending ไม่ใช่ 404)
  //    — กลืนเป็น null เฉย ๆ: หัวเคสเพิ่งตอบมาว่าเคสมีจริง 404 ตรงนี้จึงเป็นเรื่อง
  //    ฝั่ง server ที่หน้าจอช่วยอะไรไม่ได้ · แสดง "ยังไม่มีผลประเมิน" ตรงกว่าหน้าแดงทั้งหน้า
  let evaluation = null;
  let evaluationError = null;
  try {
    evaluation = await getCaseEvaluation(caseNo);
  } catch (err) {
    if (err.httpStatus === 404) evaluation = null;
    else evaluationError = err;
  }

  // Q9 — บรรทัดที่ LLM อ้างเป็นหลักฐานตอนหักคะแนน → ไฮไลต์สีส้มใน transcript
  const flaggedLineIds = new Set((evaluation && evaluation.flaggedLineIds) || []);

  // ⚠️ transcript สิทธิ์แคบกว่าหัวเคส — role "user" จะได้ 403 ตรงนี้
  //    ต้องไม่ทำให้ทั้งหน้าพัง แค่แสดงว่าไม่มีสิทธิ์ดูบทสนทนา
  let transcript = [];
  let transcriptError = null;
  try {
    const t = await getTranscript(caseNo);
    transcript = (t.lines || []).map((l) => ({
      // Q9 — id คงที่ของบรรทัด (cc.QaTranscriptLines.Id) ให้จับคู่กับ evidenceLineIds
      // ของหัวข้อที่หักคะแนนได้ ไม่ใช่เทียบด้วยลำดับแถวซึ่งเพี้ยนทันทีที่มี attachment
      lineId: l.lineId,
      speaker: l.speaker,
      senderName: l.senderName,
      text: l.text,
      timestamp: l.timestamp,
      attachmentCount: l.attachmentCount,
      isFlagged: flaggedLineIds.has(l.lineId),
    }));
  } catch (err) {
    transcriptError = err;
  }

  // Q9 — เส้นอารมณ์จริงจาก cc.QaSentimentRuns · null ได้เป็นเรื่องปกติ (ฟีเจอร์เพิ่งเปิด /
  // เคสเก่าก่อนเปิด / ยังไม่ถึงรอบแรก) — แผงอารมณ์แค่ไม่แสดง ไม่ใช่ error
  let sentiment = null;
  try {
    sentiment = await getCaseSentiment(caseNo);
  } catch {
    sentiment = null;
  }

  // QA Recommend — ของจริงเช่นกัน แต่ยังไม่มีในทุกเคส (เพิ่งเปิดใช้ / ยังไม่ถึงรอบแรก)
  //
  // ⚠️ ต้องกลืน error ทุกชนิดเหมือน transcript — 404 คือ "เคสนี้ไม่มีคำแนะนำ" ซึ่งเป็นเรื่องปกติ
  //    ไม่ใช่ความผิดพลาด · ถ้าปล่อยขึ้นไป หน้ารายละเอียดเคสจะพังทั้งหน้าสำหรับทุกเคสเก่า
  //
  // ⚠️ ส่ง **caseNo** ไม่ใช่ conversationId — เปลี่ยนเมื่อ 24 ส.ค. 2026 ด้วยเหตุผลด้านข้อมูลส่วนบุคคล
  //
  //    caseKey เดินทางใน **path ของ URL** (`cases/{caseKey}/recommend`) ซึ่งถูกเก็บ log
  //    แทบทุกชั้นโดยปริยาย: ประวัติเบราว์เซอร์ · access log ของ web server · reverse proxy ·
  //    App Insights (ระบบนี้ไม่มี ITelemetryInitializer สักตัว URL จึงเข้าไปดิบ ๆ)
  //
  //    เคสที่ Salesforce ยังไม่ตอบกลับมีค่า conversationId เป็น `pending:…:{endUserKey}`
  //    ซึ่ง **ฝัง LINE userId ดิบของลูกค้า** ไว้ข้างใน → ส่งค่านั้นใน URL = userId ไปนอนใน log
  //    หลายระบบทุกครั้งที่มีคนเปิดหน้ารายละเอียดเคสนั้น
  //
  //    ⚠️ เหตุผลเดิม ("ฟีเจอร์นี้ผูกด้วยคีย์ของบทสนทนาซึ่งมีมาตั้งแต่ช่วง pre-chat") **ล้าสมัยแล้ว**
  //    — server แปลงเป็น EndUserKey ให้เสมอไม่ว่าจะระบุเคสมาด้วยคีย์แบบไหน
  //    (QaConsoleBAL.ResolveCaseKeyAsync → GetCaseResultAsync ที่ผูกด้วย EndUserKey ล้วน)
  //    caseNo จึงได้ผลเหมือนกันเป๊ะ · คำแนะนำช่วง pre-chat ไม่หายไปไหน
  //    และตัว poller ในหน้าเดียวกัน (QACaseDetail.vue) ก็ส่ง caseNo มาตั้งแต่แรกอยู่แล้ว
  let recommend = null;
  try {
    recommend = await recommendApi.getCaseRecommend(caseNo);
  } catch {
    recommend = null;
  }

  // Q11 — สรุปการสนทนาของจริง (ฉบับ AI + ฉบับที่ agent แก้เอง) · สิทธิ์แคบเท่า transcript
  // → role ที่ไม่มีสิทธิ์ได้ 403 ตรงนี้ ต้องกลืนเป็น null (การ์ดแสดงสถานะว่าง) ไม่ใช่พังทั้งหน้า
  let callSummary = null;
  try {
    callSummary = await getCaseSummary(caseNo);
  } catch {
    callSummary = null;
  }

  // Q6 — Next best action ของจริง · สรุปด้านซ้ายก็ของจริงแล้ว (Q11 — จาก cc.QaCaseSummaries
  // ไม่ใช่ตัวสร้างข้อมูลจำลองแบบก่อน ที่เคยโชว์สรุปของเคสอื่นที่ hash มาเจอ)
  let assist = null;
  try {
    const nba = await getCaseNextBestActions(caseNo);
    assist = {
      // โครง 5 ช่องเดิมที่ QaAssistPanel อ่านอยู่แล้ว (reason/issues/actions/commitments/followUp)
      summary: (callSummary && callSummary.ai) || null,
      // สถานะ + ฉบับ agent — ให้การ์ดอธิบายได้ว่า "ว่างเพราะอะไร" และเทียบสองฉบับได้
      callSummary,
      recommendations: [],
      nextBestActions: (nba.items || []).map(toNbaCard),
      isLiveNba: true,
    };
  } catch {
    // สิทธิ์ไม่พอ / endpoint ล่ม — ไม่มีการ์ด ดีกว่าการ์ดที่โกหก
    assist = null;
  }

  return {
    // ── หัวเคส — ของจริงทั้งใบรวมช่องคะแนน Q9 · ห้ามเขียนทับด้วยค่ายืม/ค่าเดา ──
    header: toListItem(detail.header),
    participants: detail.participants || [],
    transcript,
    transcriptError,
    recommend,

    // ── Q9 — ผลประเมินจริง ─────────────────────────────────────────────────
    // ส่ง payload ทั้งก้อนให้หน้าจอ (evalStatus/skipReason/needsHumanReview/
    // reviewReasons/gatesChecked/scoredAt ฯลฯ) + แตกช่องที่ component เดิมอ่านอยู่แล้ว
    // — null/ว่างเมื่อยังไม่มีผล ไม่ใช่ข้อมูลแทนที่
    evaluation,
    evaluationError,
    rubricVersion: (evaluation && evaluation.rubricVersion) || '',
    parts: (evaluation && evaluation.parts) || [],
    gates: (evaluation && evaluation.gates) || [],
    analysis: (evaluation && evaluation.analysis) || null,
    heldBackReasons: (evaluation && evaluation.heldBackReasons) || [],

    sentiment,
    assist,
  };
}

/**
 * ทะเบียน agent ในรูปที่หน้าจออ่าน (อวาตาร์ + ตัวย่อ + จำนวนเคส)
 *
 * ⚠️ ตั้งชื่อต่างจาก `getAgents` (response ดิบ) และ `getAgentScorecard` (คะแนน) โดยตั้งใจ
 *    — สามตัวนี้คนละ endpoint คนละรูป · ทับชื่อกันเมื่อไหร่ หน้าใดหน้าหนึ่งจะอ่าน field
 *    ไม่เจอแบบเงียบ ๆ (ไม่ throw เพราะ JS คืน undefined ให้ฟรี)
 *
 * ตอนนี้ยังไม่มีหน้าไหนเรียก — เตรียมไว้ให้ dropdown กรองตามเจ้าหน้าที่ในรอบถัดไป
 */
export async function getLiveAgents(filter) {
  const rows = await getAgents({ from: filter && filter.from, to: filter && filter.to });

  return rows.map((a) => ({
    agentId: a.agentKey,
    agentName: a.displayName,
    // ⚠️ `role` ไม่ใช่ "ทีม" — เป็น Agent | Chatbot · ธนาคารไม่มีแหล่งข้อมูลทีมให้ดึง
    //    หน้าจอจึงต้องพาดหัวคอลัมน์ว่า "บทบาท" ไม่ใช่ "ทีม" (ไม่งั้นคืออ่านผิดทั้งตาราง)
    role: a.role,
    initials: initialsOf(a.displayName),
    color: colorOf(a.agentKey),
    caseCount: a.caseCount,
    openCaseCount: a.openCaseCount,
    firstSeenAt: a.firstSeenAt,
    lastSeenAt: a.lastSeenAt,
  }));
}
